import { useState, useCallback } from 'react';
import { devLog } from '../utils/envUtils';

/**
 * Hook personnalisé pour gérer l'état et la logique des formulaires d'authentification
 * @param {boolean} initialIsRegistering - Mode initial (inscription ou connexion)
 * @returns {Object} État et fonctions pour gérer le formulaire
 */
export const useAuthForm = (initialIsRegistering = false) => {
  const [isRegistering, setIsRegistering] = useState(initialIsRegistering);
  const [formData, setFormData] = useState({
    email: '',
    mot_de_passe: '',
    nom: '',
    prenom: ''
  });

  /**
   * Gère les changements d'input dans le formulaire
   */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  /**
   * Bascule entre mode inscription et connexion
   */
  const toggleMode = useCallback(() => {
    devLog('🔄 Changement de mode:', isRegistering ? 'Inscription -> Connexion' : 'Connexion -> Inscription');
    
    setIsRegistering(!isRegistering);
    // Réinitialiser le formulaire lors du changement de mode
    setFormData({
      email: '',
      mot_de_passe: '',
      nom: '',
      prenom: ''
    });
  }, [isRegistering]);

  /**
   * Réinitialise complètement le formulaire
   */
  const resetForm = useCallback(() => {
    setFormData({
      email: '',
      mot_de_passe: '',
      nom: '',
      prenom: ''
    });
  }, []);

  /**
   * Valide les données du formulaire
   */
  const validateForm = useCallback(() => {
    const { email, mot_de_passe, nom, prenom } = formData;
    
    if (!email || !mot_de_passe) {
      return { isValid: false, error: 'Email et mot de passe sont requis' };
    }
    
    if (isRegistering && (!nom || !prenom)) {
      return { isValid: false, error: 'Nom et prénom sont requis pour l\'inscription' };
    }
    
    return { isValid: true, error: null };
  }, [formData, isRegistering]);

  /**
   * Prépare les données pour la soumission
   */
  const getSubmissionData = useCallback(() => {
    if (isRegistering) {
      devLog('🔍 Inscription avec données:', {
        email: formData.email,
        nom: formData.nom,
        prenom: formData.prenom
      });
      return {
        ...formData
      };
    } else {
      devLog('🔍 Connexion avec données:', {
        email: formData.email
      });
      return {
        email: formData.email,
        mot_de_passe: formData.mot_de_passe
      };
    }
  }, [formData, isRegistering]);

  return {
    // État
    isRegistering,
    formData,
    
    // Actions
    handleInputChange,
    toggleMode,
    resetForm,
    validateForm,
    getSubmissionData,
    
    // Setters directs (pour compatibilité)
    setIsRegistering,
    setFormData
  };
};
